import { useEffect, useMemo, useState } from "react";
import { Loading } from "@cpnw/ui";
import { AdminContainer } from "hooks/useAdmin";
import StudentListTable from "components/StudentList/StudentListTable";
import DashboardSearch from "components/DashboardSearch";
import ConfirmServiceModal from "components/ConfirmServiceModal";
import styles from "./BackgroundWatch.module.css";
import { HeaderSection } from "@cpnw/ui";
import DashboardTablePagination from "components/DashboardTablePagination";
import { BackgroundCheckTypes, ContactCheckStatuses, WatchCheckTypes } from "pages/enums";
import { StaticRoutes } from "pages/routes";
// import { StudentInformationModal } from "components/StudentInformationModal";
import { DashboardContactInformationModal } from "components/DashboardContactInformationModal";

const ConfirmationDescription = {
  normal: `Once confirmed, your selected services will be initiated and associated costs will be incurred. These charges are non-refundable.`,
  checkrWithin7days: `A background check link has already been sent and is still active. You can resend a new link after it expires on [BackgroundCheckSubmittedOn].`,
  checkrAfter7days: `The last email was sent on [BackgroundCheckSubmittedOn]. Are you sure you want to send it again?`
};

const BackgroundWatch = () => {
  const { studentsData, refetchStudents, backgroundCheckAndWatchPost } = AdminContainer.useContainer();
  const [students, setStudents] = useState([]);

  const [modalDescription, setModalDescription] = useState(ConfirmationDescription.normal);
  const [selectedAll, setSelectedAll] = useState(false);

  const formatStudent = (student, defaultStatus = "na") => {
    const isCheckr = student.isCheckr || student.BackgroundCheckSubmittedOn !== null;
    const isCPNW = student.isCPNW || student.WatchCheckSubmittedOn !== null;
    const isSelected = isCheckr || isCPNW;
    const backgroundCheckSubmittedOn = (student.BackgroundCheckTypeId == BackgroundCheckTypes.Checkr && student.BackgroundCheckSubmittedOn) ? new Date(student.BackgroundCheckSubmittedOn).toLocaleDateString() : "";
    const watchCheckSubmittedOn = (student.WatchCheckTypeId == WatchCheckTypes.CPNW && student.WatchCheckSubmittedOn) ? new Date(student.WatchCheckSubmittedOn).toLocaleDateString() : "";
    const backgroundCheckTypeId = student.BackgroundCheckTypeId;
    const watchCheckTypeId = student.WatchCheckTypeId;
    const status = (backgroundCheckSubmittedOn || watchCheckSubmittedOn) ? 'sent' : defaultStatus;

    const checkrDisabled = student.BackgroundCheckTypeId == BackgroundCheckTypes.Checkr && student.BackgroundCheckSubmittedOn !== null
    const watchDisabled = student.WatchCheckTypeId == WatchCheckTypes.CPNW && student.WatchCheckSubmittedOn !== null;
    const checkboxDisabled = checkrDisabled || watchDisabled;
    return {
      ...student,
      id: student.ContactIDPK,
      name: `${student.NameLast}, ${student.NameFirst}`, // Combine NameFirst and NameLast
      program: student.BusinessUnitName,
      // isSelected,
      // isCheckr,
      // isCPNW,
      status,
      backgroundCheckSubmittedOn,
      watchCheckSubmittedOn,
      backgroundCheckTypeId,
      watchCheckTypeId,
      checkrDisabled,
      watchDisabled,
      checkboxDisabled
    };
  }

  const convertApiResponseToStudents = apiResponse => {
    const students = apiResponse?.map(student => {
      // const isCheckr = student.BackgroundCheckTypeStatusId !== 1;
      // const isCPNW = student.WatchCheckTypeStatusId !== 1;
      // const isSelected = isCheckr || isCPNW;
      return formatStudent(student);
    });

    return students;
  };

  const formattedStudents = useMemo(() => {
    return convertApiResponseToStudents(studentsData);
  }, [studentsData]);

  useEffect(() => {
    refetchStudents()
  }, [])

  useEffect(() => {
    if (formattedStudents && formattedStudents.length > 0) {
      setStudents(formattedStudents);
    } else {
      setStudents([]);
    }

    // Cleanup function
    return () => {
      setStudents([]);
    };
  }, [formattedStudents]);

  const [sentStudents, setSentStudents] = useState([]); // Track students changed to 'sent'
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [items, setItems] = useState([]);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(students.length);
  const [limit, setLimit] = useState(10); // Default limit
  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isStudentInfoModalOpen, setIsStudentInfoModalOpen] = useState(false);

  // Search states
  const [searchStudent, setSearchStudent] = useState("");
  const [searchStatus, setSearchStatus] = useState("");
  const [searchProgram, setSearchProgram] = useState("");
  const [searchAnything, setSearchAnything] = useState("");

  /** Function to filter assignments based on name, status, program, anything */
  const onFilter = (searchStudent, searchStatus, searchProgram, searchAnything) => {
    return students.filter(student => {
      // Convert filter values and assignment properties to lowercase for case-insensitive comparison
      const name = student.name?.toLowerCase();
      const status = student.status?.toLowerCase();
      const program = student.program?.toLowerCase();

      // If searchAnything is provided, check if it matches any of the three fields
      if (searchAnything) {
        const searchValue = searchAnything.toLowerCase();
        if (!name.includes(searchValue) && !status.includes(searchValue) && !program.includes(searchValue)) {
          return false;
        }
      } else {
        // Check if name matches
        if (searchStudent && !name.includes(searchStudent.toLowerCase())) {
          return false;
        }
        // Check if status matches
        if (searchStatus && !status.includes(searchStatus.toLowerCase())) {
          return false;
        }
        // Check if program matches
        if (searchProgram && !program.includes(searchProgram.toLowerCase())) {
          return false;
        }
      }
      // If all conditions pass, return true to include the assignment
      return true;
    });
  };

  const onPageChange = (e) => {
    setCurrentPage(e);
  }

  const handleCheckboxChange = (student, prop) => {
    const updatedItems = items.map(s => {
      // Update the student object based on the changed property
      if (s.id === student.id) {
        const updatedStudent = {
          ...s,
          [prop]: !s[prop],
        };

        // If prop is "isSelected", set isCheckr and isCPNW based on isSelected
        if (prop === "isSelected") {
          updatedStudent.isCheckr = updatedStudent.BackgroundCheckTypeId == BackgroundCheckTypes.Checkr ? !s[prop] : false;
          updatedStudent.isCPNW = updatedStudent.WatchCheckTypeId == WatchCheckTypes.CPNW ? !s[prop] : false;
        }

        // If either isCheckr or isCPNW is true, set isSelected to true
        if (updatedStudent.isCheckr && updatedStudent.isCPNW) {
          updatedStudent.isSelected = true;
        }
        else if (updatedStudent.isCheckr && updatedStudent.WatchCheckTypeId != WatchCheckTypes.CPNW) {
          updatedStudent.isSelected = true;
        }
        else if (updatedStudent.isCPNW && updatedStudent.BackgroundCheckTypeId != BackgroundCheckTypes.Checkr) {
          updatedStudent.isSelected = true;
        }

        if (updatedStudent.isCheckr || updatedStudent.isCPNW) {
          updatedStudent.isSelected = true;
          updatedStudent.status = updatedStudent.status == "na" ? "initiate" : updatedStudent.status;
        }

        // If both isCheckr and isCPNW are false, set isSelected to false
        if (!updatedStudent.isCheckr && !updatedStudent.isCPNW) {
          updatedStudent.isSelected = false;
          updatedStudent.status = updatedStudent.status == "initiate" ? "na" : updatedStudent.status;
        }

        return updatedStudent;
      }
      return s;
    })

    // Filter only the selected students
    const updatedStudent = updatedItems.find(s => s.id === student.id);
    setSelectedStudents(prev => {
      const others = prev.filter(s => s.id !== student.id);
      return updatedStudent.isSelected ? [...others, updatedStudent] : others;
    });
    setItems(updatedItems);
  };

  const handleStatusChange = (student, reason, type) => {
    setIsModalOpen(true);
    if (reason === "resend") {
      if (type === "checkr") {
        student.isCheckr = true;
      }
      else if (type === "cpnw") {
        student.isCPNW = true;
      }
      // Set the selected student to the one being clicked
      setSelectedStudents([student]);
    }
    else {
      // Add the student to selectedStudents if it's not already in the list
      setSelectedStudents(prevSelectedStudents => {
        if (!prevSelectedStudents.some(s => s.id === student.id)) {
          return [...prevSelectedStudents, student];
        }
        return prevSelectedStudents; // No change if the student is already selected
      });
    }

  };

  const handleSelectAll = (value) => {
    setSelectedAll(value);
    console.log("handleSelectAll: ", value);

    // Declare filtered students
    const filtered = onFilter(searchStudent, searchStatus, searchProgram, searchAnything);

    const updatedItems = items.map(student => {
      const newStudent = {
        ...student,
        isSelected: value,  // Set isSelected based on the value (true or false)
        isCheckr: student.BackgroundCheckTypeId ? value : false,    // Update isCheckr based on the value
        isCPNW: student.WatchCheckTypeId ? value : false,      // Update isCPNW based on the value
      }
      return formatStudent(newStudent, value ? "initiate" : newStudent.status)  // Set status to "initiate" if true, else "na");
    });

    // Filter only the selected students
    // setSelectedStudents(updatedItems.filter(student => student.isSelected));
    const merged = value
      ? [
        ...selectedStudents.filter(s => !filtered.some(f => f.id === s.id)),
        ...updatedItems,
      ]
      : selectedStudents.filter(s => !filtered.some(f => f.id === s.id));

    setSelectedStudents(merged);
    setItems(updatedItems);
  };

  const formatDOB = (rawDOB) => {
    const date = new Date(rawDOB);
    const formatted = `${(date.getMonth() + 1).toString().padStart(2, '0')}/${date.getDate().toString().padStart(2, '0')}/${date.getFullYear()}`;
    return formatted;
  }

  const formatAddress = (address1, city, state, zip) => {
    const parts = [address1, city, state, zip].filter(Boolean); // removes undefined, null, empty
    return parts.join(', ');
  };

  const handleStudentNameClick = (student) => {
    const selectedStudent = {
      name: student.name,
      dob: formatDOB(student.DOB),
      phone: student.CellPhone,
      email: student.email,
      address: formatAddress(student.Address1, student.City, student.State, student.Zip),
      emergencyContact: student.EmergencyContact,
      emergencyContactPhone: student.EmergencyPhone,
      school: student.School,
      program: student.program,
      sIDEID: student.IndividualID,
      ssn: student.SSN,
    }
    setSelectedStudent(selectedStudent);
    setIsStudentInfoModalOpen(true);

  };

  const onCloseStudentInfoModal = () => {
    setIsStudentInfoModalOpen(false);
    setSelectedStudent(null);
  };



  const handleConfirmInitiation = () => {
    let newlySentStudents = [];
    if (selectedStudents.length > 0) {
      const updatedSelectedItems = selectedStudents.map(s => {
        // Check if the student is selected, has isSelected = true, and their status is not already 'sent'
        // if (s.isSelected && s.status !== 'sent') {
        if (s.isCheckr || s.isCPNW) {
          // Change the status to 'sent' for the selected student
          const newlySentStudent = { ...s, status: 'sent' }
          newlySentStudents = [...newlySentStudents, newlySentStudent]
          return newlySentStudents;
        }
        return s; // No change for students already marked as 'sent' or not selected
      });
      setSelectedStudents(updatedSelectedItems);

      const updatedItems = items.map(student => {
        const selectedStudent = newlySentStudents.find(s => s.id === student.id);

        return selectedStudent ? { ...student, ...selectedStudent } : student;
      });

      setItems(updatedItems);
      setSentStudents(newlySentStudents); // Add newly sent students to track list
      handleInitiate(newlySentStudents);
      console.log("Sent selected items: ", newlySentStudents);

      //reset student selection to false
      handleSelectAll(false);
    } else {
      console.log("No students selected.");
    }
    setIsModalOpen(false);
  };

  const handleInitiate = async (students) => {
    if (!students || students.length === 0) {
      return;
    }

    try {
      const updatedItems = students.map(student => {
        if (student.isCheckr) {
          student.BackgroundCheckTypeStatusId = ContactCheckStatuses.Submitted;
        }
        if (student.isCPNW) {
          student.WatchCheckTypeStatusId = ContactCheckStatuses.Submitted;
        }
        return student;
      });

      console.log("Loading ", new Date());

      const result = await backgroundCheckAndWatchPost(updatedItems);
      await refetchStudents();

      console.log("Done", new Date());
    } catch (error) {
      console.error(error);
    } finally {

    }
  };

  useEffect(() => {
    let filteredStudents = onFilter(searchStudent, searchStatus, searchProgram, searchAnything)
      .map(student => {
        const selectedStudent = selectedStudents.find(s => s.id === student.id);
        return selectedStudent ? { ...student, ...selectedStudent } : { ...student, isSelected: false };
      });

    const startIndex = (currentPage - 1) * limit;
    const paginatedItems = filteredStudents.slice(startIndex, startIndex + limit);

    setItems(paginatedItems);
    setTotalItems(filteredStudents.length);
  }, [searchStudent, searchStatus, searchProgram, searchAnything, currentPage, limit, students, selectedStudents]);


  useEffect(() => {
    const checkrWithin7days = selectedStudents.filter(student => {
      if (!student.isCheckr) return false;
      if (!student.BackgroundCheckSubmittedOn) return false;
      const backgroundCheckSubmittedOn = new Date(student.BackgroundCheckSubmittedOn);
      const currentDate = new Date();
      const diffTime = Math.abs(currentDate - backgroundCheckSubmittedOn);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return student.isCheckr && diffDays <= 7;
    })
    const checkrAfter7days = selectedStudents.filter(student => {
      if (!student.isCheckr) return false;
      if (!student.BackgroundCheckSubmittedOn) return false;
      const backgroundCheckSubmittedOn = new Date(student.BackgroundCheckSubmittedOn);
      const currentDate = new Date();
      const diffTime = Math.abs(currentDate - backgroundCheckSubmittedOn);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return student.isCheckr && diffDays > 7;
    })
    if (checkrWithin7days.length > 0) {
      const expirationDate = new Date(checkrWithin7days[0].BackgroundCheckSubmittedOn);
      expirationDate.setDate(expirationDate.getDate() + 7);
      const formattedDate = expirationDate.toLocaleDateString();
      setModalDescription(ConfirmationDescription.checkrWithin7days.replace("[BackgroundCheckSubmittedOn]", formattedDate));
    } else if (checkrAfter7days.length > 0) {
      const expirationDate = new Date(checkrAfter7days[0].BackgroundCheckSubmittedOn);
      expirationDate.setDate(expirationDate.getDate() + 7);
      const formattedDate = expirationDate.toLocaleDateString();
      setModalDescription(ConfirmationDescription.checkrAfter7days.replace("[BackgroundCheckSubmittedOn]", formattedDate));
    } else {
      setModalDescription(ConfirmationDescription.normal);
    }
  }, [selectedStudents])

  return (
    <>
      <HeaderSection header={"Background + WATCH"} />

      {/* Search Container */}
      <div className={styles.searchContainer}>
        <DashboardSearch
          onInput={setSearchStudent}
          showLabel={false}
          placeholder="Search Student"
        />
        <DashboardSearch
          onInput={setSearchStatus}
          showLabel={false}
          placeholder="Search Status"
        />
        <DashboardSearch
          onInput={setSearchProgram}
          showLabel={false}
          placeholder="Search Program"
        />
        <DashboardSearch
          onInput={setSearchAnything}
          showLabel={false}
          placeholder="Search Anything"
        />
      </div>

      {/* Student Table */}
      <div className={styles.table}>
        <StudentListTable
          view={'background'}
          students={items}
          onCheckboxChange={handleCheckboxChange}
          onStatusChange={handleStatusChange}
          onSelectAll={handleSelectAll}
          selectedAll={selectedAll}
          onStudentNameClick={handleStudentNameClick}
        // studentStatuses={studentStatuses}
        // onInitiate={handleInitiate}
        // onRadioSelection={handleRadioSelection}
        />
      </div>
      <div className={styles.paginationContainer}>
        <DashboardTablePagination onPageChange={(e) => onPageChange(e)} total={totalItems}
          currentPage={currentPage}
          isAutoSetPage={false}
          perPage={limit} />
      </div>

      {/* Confirm Service Modal */}
      {isModalOpen && (
        <ConfirmServiceModal
          modalDescription={modalDescription}
          onClose={() => setIsModalOpen(false)}
          onCancel={() => {
            console.log("Cancelled service initiation");
            setIsModalOpen(false);
          }}
          onConfirm={() => {
            console.log("Service initiated successfully!");
            setIsModalOpen(false);
            handleConfirmInitiation(); // Change status to "sent"
          }}
        />
      )}
      {isStudentInfoModalOpen && (
        <DashboardContactInformationModal
          isOpen={isStudentInfoModalOpen}
          onClose={() => { onCloseStudentInfoModal() }}
          contact={selectedStudent}
          headerTitle={"Student Information"} />
      )}

    </>
  );
};

export default BackgroundWatch;
